<script setup>
import { ref, computed, onBeforeMount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useContactFilterContext } from 'dashboard/components-next/filter/contactProvider.js';
import { useSnakeCase, useCamelCase } from 'dashboard/composables/useTransformKeys';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { filterTypes } = useContactFilterContext();

const uiFlags = useMapGetter('audiences/getUIFlags');

const audienceId = computed(() => route.params.audienceId);
const isEditing = computed(() => !!audienceId.value);

const name = ref('');
const description = ref('');
const status = ref('active');
const filters = ref([{ ...DEFAULT_FILTER }]);
const contactsCount = ref(null);
const isLoadingCount = ref(false);
const conditionsRef = ref([]);

const pageTitle = computed(() =>
  isEditing.value
    ? t('AUDIENCE_MGMT.FORM.EDIT_TITLE')
    : t('AUDIENCE_MGMT.FORM.CREATE_TITLE')
);

const isSubmitting = computed(() =>
  uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    filters.value = [{ ...DEFAULT_FILTER }];
  } else {
    filters.value.splice(index, 1);
  }
};

const isConditionsValid = () => {
  if (!conditionsRef.value?.length) return false;
  return conditionsRef.value.every(condition => condition?.validate?.());
};

const fetchContactsCount = async () => {
  if (!isConditionsValid()) return;
  isLoadingCount.value = true;
  try {
    const payload = useSnakeCase(
      JSON.parse(JSON.stringify(filters.value))
    );
    const queryPayload = filterQueryGenerator(payload);
    const response = await store.dispatch('contacts/filter', {
      page: 1,
      sortAttr: '-last_activity_at',
      queryPayload,
    });
    contactsCount.value = store.getters['contacts/getMeta']?.count || 0;
  } catch (error) {
    contactsCount.value = null;
  } finally {
    isLoadingCount.value = false;
  }
};

const goBack = () => {
  router.push(accountScopedRoute('audiences_index'));
};

const handleSubmit = async () => {
  if (!name.value.trim()) {
    useAlert(t('AUDIENCE_MGMT.FORM.NAME.REQUIRED_ERROR'));
    return;
  }
  if (!isConditionsValid()) {
    useAlert(t('AUDIENCE_MGMT.FORM.FILTERS.REQUIRED_ERROR'));
    return;
  }

  const filtersPayload = useSnakeCase(
    JSON.parse(JSON.stringify(filters.value))
  );

  const audienceData = {
    name: name.value.trim(),
    description: description.value.trim(),
    status: status.value,
    filters: filtersPayload,
  };

  try {
    if (isEditing.value) {
      await store.dispatch('audiences/update', {
        id: audienceId.value,
        ...audienceData,
      });
      useAlert(t('AUDIENCE_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('audiences/create', audienceData);
      useAlert(t('AUDIENCE_MGMT.ADD.API.SUCCESS_MESSAGE'));
    }
    goBack();
  } catch (error) {
    const errorMessage = isEditing.value
      ? t('AUDIENCE_MGMT.EDIT.API.ERROR_MESSAGE')
      : t('AUDIENCE_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(error?.message || errorMessage);
  }
};

const loadAudience = async () => {
  if (!isEditing.value) return;

  await store.dispatch('audiences/get');
  const audience = store.getters['audiences/getAudienceById'](audienceId.value);
  if (audience?.id) {
    name.value = audience.name;
    description.value = audience.description || '';
    status.value = audience.status;
    filters.value = useCamelCase(audience.filters || []).map(f => ({
      ...DEFAULT_FILTER,
      ...f,
    }));
  }
};

onBeforeMount(loadAudience);
</script>

<template>
  <div class="flex flex-col flex-1 h-full m-0 overflow-auto bg-n-surface-1">
    <div class="flex flex-col max-w-3xl gap-6 p-6">
      <div class="flex items-center gap-4">
        <Button
          icon="i-lucide-arrow-left"
          slate
          sm
          @click="goBack"
        />
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ pageTitle }}
        </h1>
      </div>

      <div class="grid gap-4">
        <Input
          v-model="name"
          :label="$t('AUDIENCE_MGMT.FORM.NAME.LABEL')"
          :placeholder="$t('AUDIENCE_MGMT.FORM.NAME.PLACEHOLDER')"
        />

        <Input
          v-model="description"
          :label="$t('AUDIENCE_MGMT.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('AUDIENCE_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
        />

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('AUDIENCE_MGMT.FORM.STATUS.LABEL') }}
          </label>
          <select
            v-model="status"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option value="active">{{ $t('AUDIENCE_MGMT.STATUS.ACTIVE') }}</option>
            <option value="inactive">{{ $t('AUDIENCE_MGMT.STATUS.INACTIVE') }}</option>
          </select>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('AUDIENCE_MGMT.FORM.FILTERS.TITLE') }}
        </h3>

        <ul class="grid gap-4 list-none">
          <template v-for="(filter, index) in filters" :key="index">
            <ConditionRow
              v-if="index === 0"
              ref="conditionsRef"
              v-model:attribute-key="filter.attributeKey"
              v-model:filter-operator="filter.filterOperator"
              v-model:values="filter.values"
              :filter-types="filterTypes"
              :show-query-operator="false"
              @remove="removeFilter(index)"
            />
            <ConditionRow
              v-else
              ref="conditionsRef"
              v-model:attribute-key="filter.attributeKey"
              v-model:filter-operator="filter.filterOperator"
              v-model:query-operator="filters[index - 1].queryOperator"
              v-model:values="filter.values"
              show-query-operator
              :filter-types="filterTypes"
              @remove="removeFilter(index)"
            />
          </template>
        </ul>

        <div class="flex items-center gap-4">
          <Button sm ghost blue @click="addFilter">
            {{ $t('AUDIENCE_MGMT.FORM.FILTERS.ADD') }}
          </Button>
          <Button sm faded slate :is-loading="isLoadingCount" @click="fetchContactsCount">
            {{ $t('AUDIENCE_MGMT.FORM.FILTERS.COUNT_CONTACTS') }}
          </Button>
          <span v-if="contactsCount !== null" class="text-sm text-n-slate-11">
            {{ $t('AUDIENCE_MGMT.FORM.FILTERS.CONTACTS_FOUND', { count: contactsCount }) }}
          </span>
          <Spinner v-if="isLoadingCount" class="ml-2" />
        </div>
      </div>

      <div class="flex gap-3 pt-4 border-t border-n-weak">
        <Button sm faded slate @click="goBack">
          {{ $t('AUDIENCE_MGMT.FORM.CANCEL') }}
        </Button>
        <Button sm solid blue :is-loading="isSubmitting" @click="handleSubmit">
          {{ isEditing
            ? $t('AUDIENCE_MGMT.FORM.UPDATE')
            : $t('AUDIENCE_MGMT.FORM.CREATE') }}
        </Button>
      </div>
    </div>
  </div>
</template>
